import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { decryptData, encryptData, generatePassword, calculatePasswordStrength } from '@/lib/crypto';
import { Lock, Plus, Search, Eye, EyeOff, Copy, ExternalLink, Settings as SettingsIcon, AlertCircle, Key, Edit2, Trash2, X, RefreshCw, Check, AlertTriangle, ChevronDown, ChevronRight, Folder, FolderOpen } from 'lucide-react';

interface DecryptedEntry {
  id: string;
  title: string;
  username: string;
  password: string;
  url?: string;
  notes?: string;
  category: string;
}

export default function Vault() {
  const navigate = useNavigate();
  const { user, encryptionKey, isVaultUnlocked, unlockVault } = useAuth();
  const [entries, setEntries] = useState<DecryptedEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<DecryptedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [visiblePasswords, setVisiblePasswords] = useState<Set<string>>(new Set());
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [masterPassword, setMasterPassword] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<DecryptedEntry | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editCategory, setEditCategory] = useState('General');
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [passwordLength, setPasswordLength] = useState(16);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState<DecryptedEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [showAllItems, setShowAllItems] = useState(false);

  useEffect(() => {
    const savedExpandedFolders = localStorage.getItem('expandedFolders');
    if (savedExpandedFolders) {
      try {
        setExpandedFolders(new Set(JSON.parse(savedExpandedFolders)));
      } catch (err) {
        console.error('Failed to load expanded folders:', err);
      }
    }
  }, []);

  useEffect(() => {
    if (user && isVaultUnlocked && encryptionKey) {
      loadEntries();
    } else {
      setLoading(false);
    }
  }, [user, isVaultUnlocked, encryptionKey]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredEntries(entries);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = entries.filter(
        entry =>
          entry.title.toLowerCase().includes(query) ||
          entry.username.toLowerCase().includes(query) ||
          entry.category.toLowerCase().includes(query) ||
          entry.url?.toLowerCase().includes(query)
      );
      setFilteredEntries(filtered);

      const categoriesToExpand = new Set(filtered.map(entry => entry.category));
      setExpandedFolders(categoriesToExpand);
    }
  }, [searchQuery, entries]);

  const loadEntries = async () => {
    if (!encryptionKey || !user) return;

    setLoading(true);
    setError('');

    try {
      const { data, error: fetchError } = await supabase
        .from('password_entries')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (fetchError) throw fetchError;

      const decryptedEntries = await Promise.all(
        (data || []).map(async entry => {
          try {
            const decrypted = await decryptData(
              {
                encrypted: entry.encrypted_data,
                iv: entry.iv,
              },
              encryptionKey
            );

            return {
              id: entry.id,
              ...decrypted,
              category: entry.category || 'General',
            };
          } catch (err) {
            console.error('Failed to decrypt entry:', entry.id, err);
            return null;
          }
        })
      );

      setEntries(decryptedEntries.filter((e): e is DecryptedEntry => e !== null));
    } catch (err) {
      console.error('Error loading entries:', err);
      setError('Failed to load vault entries');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setUnlocking(true);

    try {
      const result = await unlockVault(masterPassword);
      if (result.success) {
        setMasterPassword('');
        await loadEntries();
      } else {
        setError(result.error || 'Failed to unlock vault');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setUnlocking(false);
    }
  };

  const togglePasswordVisibility = (id: string) => {
    setVisiblePasswords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleEditClick = (entry: DecryptedEntry) => {
    setEditingEntry(entry);
    setEditTitle(entry.title);
    setEditUsername(entry.username);
    setEditPassword(entry.password);
    setEditUrl(entry.url || '');
    setEditNotes(entry.notes || '');
    setEditCategory(entry.category);
    setShowEditModal(true);
    setSaveSuccess(false);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingEntry(null);
    setEditTitle('');
    setEditUsername('');
    setEditPassword('');
    setEditUrl('');
    setEditNotes('');
    setEditCategory('General');
    setSaveSuccess(false);
  };

  const handleGeneratePassword = () => {
    const generated = generatePassword(passwordLength, {
      uppercase: includeUppercase,
      lowercase: includeLowercase,
      numbers: includeNumbers,
      symbols: includeSymbols,
    });
    setEditPassword(generated);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingEntry || !user || !encryptionKey) {
      setError('Cannot save: missing required data');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const entryData = {
        title: editTitle,
        username: editUsername,
        password: editPassword,
        url: editUrl || undefined,
        notes: editNotes || undefined,
      };

      const encrypted = await encryptData(entryData, encryptionKey);

      const { error: updateError } = await supabase
        .from('password_entries')
        .update({
          encrypted_data: encrypted.encrypted,
          iv: encrypted.iv,
          category: editCategory,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingEntry.id)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setSaveSuccess(true);
      setTimeout(async () => {
        handleCloseEditModal();
        await loadEntries();
      }, 1500);
    } catch (err) {
      console.error('Error updating entry:', err);
      setError(err instanceof Error ? err.message : 'Failed to update entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (entry: DecryptedEntry) => {
    setDeletingEntry(entry);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingEntry(null);
  };

  const handleConfirmDelete = async () => {
    if (!user || !deletingEntry) {
      return;
    }

    setDeleting(true);
    setError('');

    try {
      const { error: deleteError } = await supabase
        .from('password_entries')
        .delete()
        .eq('id', deletingEntry.id)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      handleCloseDeleteModal();
      await loadEntries();
    } catch (err) {
      console.error('Error deleting entry:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete entry');
    } finally {
      setDeleting(false);
    }
  };

  const groupEntriesByCategory = () => {
    const grouped: Record<string, DecryptedEntry[]> = {};
    filteredEntries.forEach(entry => {
      const category = entry.category || 'Uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(entry);
    });
    return grouped;
  };

  const toggleFolder = (category: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedFolders(newExpanded);
    localStorage.setItem('expandedFolders', JSON.stringify(Array.from(newExpanded)));
  };

  const expandAll = () => {
    const allCategories = new Set(Object.keys(groupEntriesByCategory()));
    setExpandedFolders(allCategories);
    localStorage.setItem('expandedFolders', JSON.stringify(Array.from(allCategories)));
  };

  const collapseAll = () => {
    setExpandedFolders(new Set());
    localStorage.setItem('expandedFolders', JSON.stringify([]));
  };

  const editPasswordStrength = calculatePasswordStrength(editPassword);
  const groupedEntries = groupEntriesByCategory();
  const categories = Object.keys(groupedEntries).sort();

  if (!isVaultUnlocked) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9fafb',
        padding: '24px'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          padding: '32px'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '64px',
              height: '64px',
              backgroundColor: '#3b82f6',
              borderRadius: '50%',
              marginBottom: '16px'
            }}>
              <Lock size={32} color="#ffffff" />
            </div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#111827',
              marginBottom: '8px'
            }}>
              Vault Locked
            </h1>
            <p style={{ fontSize: '14px', color: '#6b7280' }}>
              Enter your master password to unlock
            </p>
          </div>

          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <AlertCircle size={20} color="#ef4444" />
              <span style={{ fontSize: '14px', color: '#991b1b' }}>{error}</span>
            </div>
          )}

          <form onSubmit={handleUnlock}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '6px'
              }}>
                Master Password
              </label>
              <div style={{ position: 'relative' }}>
                <div style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none'
                }}>
                  <Key size={20} color="#9ca3af" />
                </div>
                <input
                  type="password"
                  value={masterPassword}
                  onChange={(e) => setMasterPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 44px',
                    fontSize: '14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    outline: 'none',
                    transition: 'border-color 0.15s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={unlocking}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#ffffff',
                backgroundColor: unlocking ? '#9ca3af' : '#3b82f6',
                border: 'none',
                borderRadius: '8px',
                cursor: unlocking ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => !unlocking && (e.currentTarget.style.backgroundColor = '#2563eb')}
              onMouseLeave={(e) => !unlocking && (e.currentTarget.style.backgroundColor = '#3b82f6')}
            >
              {unlocking ? 'Unlocking...' : 'Unlock Vault'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <header style={{
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Lock size={24} color="#3b82f6" />
            <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
              Secure Vault
            </h1>
          </div>
          <button
            onClick={() => navigate('/settings')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#374151',
              backgroundColor: 'transparent',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            <SettingsIcon size={16} />
            Settings
          </button>
        </div>
      </header>

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '24px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <div style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none'
            }}>
              <Search size={20} color="#9ca3af" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search entries..."
              style={{
                width: '100%',
                padding: '12px 12px 12px 44px',
                fontSize: '14px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                outline: 'none',
                transition: 'border-color 0.15s',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
          <button
            onClick={() => navigate('/add-entry')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#ffffff',
              backgroundColor: '#3b82f6',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
          >
            <Plus size={20} />
            Add Entry
          </button>
        </div>

        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <AlertCircle size={20} color="#ef4444" />
            <span style={{ fontSize: '14px', color: '#991b1b' }}>{error}</span>
          </div>
        )}

        {!loading && filteredEntries.length > 0 && !searchQuery && (
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '16px',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={() => setShowAllItems(!showAllItems)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: 500,
                color: showAllItems ? '#ffffff' : '#374151',
                backgroundColor: showAllItems ? '#3b82f6' : '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                if (!showAllItems) e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
              onMouseLeave={(e) => {
                if (!showAllItems) e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
              {showAllItems ? 'Show Folders' : 'Show All Items'}
            </button>
            {!showAllItems && (
              <>
                <button
                  onClick={expandAll}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#374151',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                >
                  Expand All
                </button>
                <button
                  onClick={collapseAll}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#374151',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                >
                  Collapse All
                </button>
              </>
            )}
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #e5e7eb',
              borderTopColor: '#3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        ) : filteredEntries.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '48px 24px',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            border: '2px dashed #d1d5db'
          }}>
            <Lock size={48} color="#9ca3af" style={{ marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
              {searchQuery ? 'No entries found' : 'No entries yet'}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
              {searchQuery ? 'Try a different search term' : 'Add your first password entry to get started'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => navigate('/add-entry')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#ffffff',
                  backgroundColor: '#3b82f6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                <Plus size={20} />
                Add Entry
              </button>
            )}
          </div>
        ) : searchQuery || showAllItems ? (
          <div style={{
            display: 'grid',
            gap: '16px'
          }}>
            {searchQuery && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#0369a1',
                marginBottom: '8px'
              }}>
                Found {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'} matching "{searchQuery}"
              </div>
            )}
            {filteredEntries.map(entry => (
              <div
                key={entry.id}
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  transition: 'box-shadow 0.15s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                      {entry.title}
                    </h3>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#3b82f6',
                      backgroundColor: '#eff6ff',
                      borderRadius: '9999px'
                    }}>
                      {entry.category}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {entry.url && (
                      <a
                        href={entry.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '8px',
                          color: '#6b7280',
                          backgroundColor: '#f9fafb',
                          borderRadius: '8px',
                          textDecoration: 'none',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Open URL"
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                    <button
                      onClick={() => handleEditClick(entry)}
                      style={{
                        padding: '8px',
                        color: '#3b82f6',
                        backgroundColor: '#eff6ff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                      title="Edit entry"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(entry)}
                      style={{
                        padding: '8px',
                        color: '#ef4444',
                        backgroundColor: '#fef2f2',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                      title="Delete entry"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div style={{ display: 'grid', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#6b7280', marginBottom: '4px' }}>
                      Username
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <code style={{
                        flex: 1,
                        padding: '8px 12px',
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        color: '#374151',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px'
                      }}>
                        {entry.username}
                      </code>
                      <button
                        onClick={() => copyToClipboard(entry.username, `${entry.id}-username`)}
                        style={{
                          padding: '8px',
                          color: copySuccess === `${entry.id}-username` ? '#22c55e' : '#6b7280',
                          backgroundColor: '#f9fafb',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                        title="Copy username"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#6b7280', marginBottom: '4px' }}>
                      Password
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <code style={{
                        flex: 1,
                        padding: '8px 12px',
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        color: '#374151',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px'
                      }}>
                        {visiblePasswords.has(entry.id) ? entry.password : '••••••••••••'}
                      </code>
                      <button
                        onClick={() => togglePasswordVisibility(entry.id)}
                        style={{
                          padding: '8px',
                          color: '#6b7280',
                          backgroundColor: '#f9fafb',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                        title={visiblePasswords.has(entry.id) ? 'Hide password' : 'Show password'}
                      >
                        {visiblePasswords.has(entry.id) ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(entry.password, `${entry.id}-password`)}
                        style={{
                          padding: '8px',
                          color: copySuccess === `${entry.id}-password` ? '#22c55e' : '#6b7280',
                          backgroundColor: '#f9fafb',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                        title="Copy password"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>

                  {entry.notes && (
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#6b7280', marginBottom: '4px' }}>
                        Notes
                      </label>
                      <p style={{
                        padding: '8px 12px',
                        fontSize: '14px',
                        color: '#374151',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px',
                        margin: 0,
                        whiteSpace: 'pre-wrap'
                      }}>
                        {entry.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gap: '12px'
          }}>
            {categories.map(category => {
              const categoryEntries = groupedEntries[category];
              const isExpanded = expandedFolders.has(category);

              return (
                <div key={category}>
                  <button
                    onClick={() => toggleFolder(category)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px 20px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {isExpanded ? (
                        <FolderOpen size={20} color="#3b82f6" />
                      ) : (
                        <Folder size={20} color="#6b7280" />
                      )}
                      <span style={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#111827'
                      }}>
                        {category}
                      </span>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '24px',
                        height: '24px',
                        padding: '0 8px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#6b7280',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '12px'
                      }}>
                        {categoryEntries.length}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {isExpanded ? (
                        <ChevronDown size={20} color="#6b7280" />
                      ) : (
                        <ChevronRight size={20} color="#6b7280" />
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div style={{
                      marginTop: '8px',
                      display: 'grid',
                      gap: '12px',
                      paddingLeft: '12px'
                    }}>
                      {categoryEntries.map(entry => (
                        <div
                          key={entry.id}
                          style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '12px',
                            padding: '20px',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            transition: 'box-shadow 0.15s',
                            border: '1px solid #e5e7eb'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                            <div>
                              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                                {entry.title}
                              </h3>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              {entry.url && (
                                <a
                                  href={entry.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    padding: '8px',
                                    color: '#6b7280',
                                    backgroundColor: '#f9fafb',
                                    borderRadius: '8px',
                                    textDecoration: 'none',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                  title="Open URL"
                                >
                                  <ExternalLink size={16} />
                                </a>
                              )}
                              <button
                                onClick={() => handleEditClick(entry)}
                                style={{
                                  padding: '8px',
                                  color: '#3b82f6',
                                  backgroundColor: '#eff6ff',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                                title="Edit entry"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(entry)}
                                style={{
                                  padding: '8px',
                                  color: '#ef4444',
                                  backgroundColor: '#fef2f2',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                                title="Delete entry"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>

                          <div style={{ display: 'grid', gap: '12px' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#6b7280', marginBottom: '4px' }}>
                                Username
                              </label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <code style={{
                                  flex: 1,
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  fontFamily: 'monospace',
                                  color: '#374151',
                                  backgroundColor: '#f9fafb',
                                  borderRadius: '6px'
                                }}>
                                  {entry.username}
                                </code>
                                <button
                                  onClick={() => copyToClipboard(entry.username, `${entry.id}-username`)}
                                  style={{
                                    padding: '8px',
                                    color: copySuccess === `${entry.id}-username` ? '#22c55e' : '#6b7280',
                                    backgroundColor: '#f9fafb',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                  }}
                                  title="Copy username"
                                >
                                  <Copy size={16} />
                                </button>
                              </div>
                            </div>

                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#6b7280', marginBottom: '4px' }}>
                                Password
                              </label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <code style={{
                                  flex: 1,
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  fontFamily: 'monospace',
                                  color: '#374151',
                                  backgroundColor: '#f9fafb',
                                  borderRadius: '6px'
                                }}>
                                  {visiblePasswords.has(entry.id) ? entry.password : '••••••••••••'}
                                </code>
                                <button
                                  onClick={() => togglePasswordVisibility(entry.id)}
                                  style={{
                                    padding: '8px',
                                    color: '#6b7280',
                                    backgroundColor: '#f9fafb',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                  }}
                                  title={visiblePasswords.has(entry.id) ? 'Hide password' : 'Show password'}
                                >
                                  {visiblePasswords.has(entry.id) ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                                <button
                                  onClick={() => copyToClipboard(entry.password, `${entry.id}-password`)}
                                  style={{
                                    padding: '8px',
                                    color: copySuccess === `${entry.id}-password` ? '#22c55e' : '#6b7280',
                                    backgroundColor: '#f9fafb',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                  }}
                                  title="Copy password"
                                >
                                  <Copy size={16} />
                                </button>
                              </div>
                            </div>

                            {entry.notes && (
                              <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#6b7280', marginBottom: '4px' }}>
                                  Notes
                                </label>
                                <p style={{
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  color: '#374151',
                                  backgroundColor: '#f9fafb',
                                  borderRadius: '6px',
                                  margin: 0,
                                  whiteSpace: 'pre-wrap'
                                }}>
                                  {entry.notes}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {showEditModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          zIndex: 1000,
          overflowY: 'auto'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative'
          }}>
            {saveSuccess ? (
              <div style={{
                padding: '48px 32px',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#22c55e',
                  borderRadius: '50%',
                  marginBottom: '16px'
                }}>
                  <Check size={32} color="#ffffff" />
                </div>
                <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
                  Entry Updated
                </h2>
                <p style={{ fontSize: '14px', color: '#6b7280' }}>
                  Changes saved successfully
                </p>
              </div>
            ) : (
              <form onSubmit={handleSaveEdit}>
                <div style={{
                  padding: '24px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                    Edit Entry
                  </h2>
                  <button
                    type="button"
                    onClick={handleCloseEditModal}
                    style={{
                      padding: '8px',
                      color: '#6b7280',
                      backgroundColor: '#f9fafb',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  >
                    <X size={20} />
                  </button>
                </div>

                <div style={{ padding: '24px' }}>
                  {error && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}>
                      <AlertCircle size={20} color="#ef4444" />
                      <span style={{ fontSize: '14px', color: '#991b1b' }}>{error}</span>
                    </div>
                  )}

                  <div style={{ display: 'grid', gap: '16px' }}>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        Title *
                      </label>
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        required
                        style={{
                          width: '100%',
                          padding: '12px',
                          fontSize: '14px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        Category *
                      </label>
                      <select
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                        required
                        style={{
                          width: '100%',
                          padding: '12px',
                          fontSize: '14px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          outline: 'none',
                          boxSizing: 'border-box',
                          backgroundColor: '#ffffff'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      >
                        <option value="General">General</option>
                        <option value="Social">Social</option>
                        <option value="Work">Work</option>
                        <option value="Finance">Finance</option>
                        <option value="Shopping">Shopping</option>
                        <option value="Entertainment">Entertainment</option>
                      </select>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        Username *
                      </label>
                      <input
                        type="text"
                        value={editUsername}
                        onChange={(e) => setEditUsername(e.target.value)}
                        required
                        style={{
                          width: '100%',
                          padding: '12px',
                          fontSize: '14px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        URL
                      </label>
                      <input
                        type="url"
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          fontSize: '14px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        Password *
                      </label>
                      <div style={{ position: 'relative', marginBottom: '8px' }}>
                        <input
                          type={showEditPassword ? 'text' : 'password'}
                          value={editPassword}
                          onChange={(e) => setEditPassword(e.target.value)}
                          required
                          style={{
                            width: '100%',
                            padding: '12px 44px 12px 12px',
                            fontSize: '14px',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            outline: 'none',
                            boxSizing: 'border-box',
                            fontFamily: 'monospace'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowEditPassword(!showEditPassword)}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            padding: '8px',
                            color: '#6b7280',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          {showEditPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {editPassword && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                          <div style={{
                            flex: 1,
                            height: '6px',
                            backgroundColor: '#e5e7eb',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              height: '100%',
                              width: `${(editPasswordStrength.score / 7) * 100}%`,
                              backgroundColor: editPasswordStrength.color,
                              transition: 'all 0.3s'
                            }} />
                          </div>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: 500,
                            color: editPasswordStrength.color
                          }}>
                            {editPasswordStrength.label}
                          </span>
                        </div>
                      )}
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <span style={{ fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                            Password Generator
                          </span>
                          <button
                            type="button"
                            onClick={handleGeneratePassword}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#ffffff',
                              backgroundColor: '#3b82f6',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                          >
                            <RefreshCw size={14} />
                            Generate
                          </button>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          <label style={{
                            display: 'block',
                            fontSize: '11px',
                            fontWeight: 500,
                            color: '#6b7280',
                            marginBottom: '4px'
                          }}>
                            Length: {passwordLength}
                          </label>
                          <input
                            type="range"
                            min="8"
                            max="32"
                            value={passwordLength}
                            onChange={(e) => setPasswordLength(parseInt(e.target.value))}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '12px',
                            color: '#374151',
                            cursor: 'pointer'
                          }}>
                            <input
                              type="checkbox"
                              checked={includeUppercase}
                              onChange={(e) => setIncludeUppercase(e.target.checked)}
                              style={{ cursor: 'pointer' }}
                            />
                            Uppercase
                          </label>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '12px',
                            color: '#374151',
                            cursor: 'pointer'
                          }}>
                            <input
                              type="checkbox"
                              checked={includeLowercase}
                              onChange={(e) => setIncludeLowercase(e.target.checked)}
                              style={{ cursor: 'pointer' }}
                            />
                            Lowercase
                          </label>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '12px',
                            color: '#374151',
                            cursor: 'pointer'
                          }}>
                            <input
                              type="checkbox"
                              checked={includeNumbers}
                              onChange={(e) => setIncludeNumbers(e.target.checked)}
                              style={{ cursor: 'pointer' }}
                            />
                            Numbers
                          </label>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '12px',
                            color: '#374151',
                            cursor: 'pointer'
                          }}>
                            <input
                              type="checkbox"
                              checked={includeSymbols}
                              onChange={(e) => setIncludeSymbols(e.target.checked)}
                              style={{ cursor: 'pointer' }}
                            />
                            Symbols
                          </label>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        Notes
                      </label>
                      <textarea
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '12px',
                          fontSize: '14px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          outline: 'none',
                          boxSizing: 'border-box',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      />
                    </div>
                  </div>
                </div>

                <div style={{
                  padding: '24px',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    type="button"
                    onClick={handleCloseEditModal}
                    disabled={saving}
                    style={{
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#374151',
                      backgroundColor: '#ffffff',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: saving ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#ffffff',
                      backgroundColor: saving ? '#9ca3af' : '#3b82f6',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: saving ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={(e) => !saving && (e.currentTarget.style.backgroundColor = '#2563eb')}
                    onMouseLeave={(e) => !saving && (e.currentTarget.style.backgroundColor = '#3b82f6')}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {showDeleteModal && deletingEntry && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            zIndex: 1000,
            overflowY: 'auto'
          }}
          onClick={handleCloseDeleteModal}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              width: '100%',
              maxWidth: '500px',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '50%'
                }}>
                  <AlertTriangle size={20} color="#ef4444" />
                </div>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                  Delete Entry
                </h2>
              </div>
              <button
                type="button"
                onClick={handleCloseDeleteModal}
                disabled={deleting}
                style={{
                  padding: '8px',
                  color: '#6b7280',
                  backgroundColor: '#f9fafb',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#e5e7eb')}
                onMouseLeave={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#f9fafb')}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              <p style={{
                fontSize: '14px',
                color: '#374151',
                marginBottom: '16px',
                lineHeight: '1.6'
              }}>
                Are you sure you want to delete <strong>{deletingEntry.title}</strong>?
              </p>
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                lineHeight: '1.6'
              }}>
                This action cannot be undone. All information associated with this entry will be permanently removed.
              </p>
            </div>

            <div style={{
              padding: '24px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={handleCloseDeleteModal}
                disabled={deleting}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#374151',
                  backgroundColor: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: deleting ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#f9fafb')}
                onMouseLeave={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#ffffff')}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleting}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#ffffff',
                  backgroundColor: deleting ? '#9ca3af' : '#ef4444',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: deleting ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#dc2626')}
                onMouseLeave={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#ef4444')}
              >
                {deleting ? 'Deleting...' : 'Delete Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
